import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { CreateGateExitRequest, GateExitDto, LoadingTaskOptionForGateExitDto } from '@/types/outbound';

export default function GateExitPage() {
  const { t } = useTranslation('gateExit');
  const { formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: gateExits = [], isLoading } = useQuery({
    queryKey: ['gate-exits'],
    queryFn: async () => (await apiClient.get<GateExitDto[]>('/gate-exits')).data,
  });
  const { data: availableLoadingTasks = [] } = useQuery({
    queryKey: ['gate-exits', 'available-loading-tasks'],
    queryFn: async () => (await apiClient.get<LoadingTaskOptionForGateExitDto[]>('/gate-exits/available-loading-tasks')).data,
  });

  const [open, setOpen] = useState(false);
  const [loadingTaskId, setLoadingTaskId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [securityRemarks, setSecurityRemarks] = useState('');

  const selectedTask = availableLoadingTasks.find((t) => t.id === loadingTaskId);

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: CreateGateExitRequest = {
        loadingTaskId,
        driverName: driverName || null,
        securityRemarks: securityRemarks || null,
      };
      return apiClient.post('/gate-exits', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gate-exits'] });
      setOpen(false);
    },
  });

  const columns: DataTableColumn<GateExitDto>[] = [
    { field: 'gateExitNumber', headerName: t('table.number') },
    { field: 'loadingTaskNumber', headerName: t('table.loadingTask') },
    { field: 'vehicleNo', headerName: t('table.vehicle'), render: (r) => r.vehicleNo ?? '—' },
    { field: 'exitDateTime', headerName: t('table.exitDateTime'), render: (r) => formatDateTime(r.exitDateTime) },
    { field: 'driverName', headerName: t('table.driverName'), render: (r) => r.driverName ?? '—' },
    { field: 'exitedByUserName', headerName: t('table.exitedBy') },
  ];

  const openDialog = () => {
    setLoadingTaskId('');
    setDriverName('');
    setSecurityRemarks('');
    queryClient.invalidateQueries({ queryKey: ['gate-exits', 'available-loading-tasks'] });
    setOpen(true);
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.GATE_EXIT_EXECUTE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={gateExits} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="gate-exits" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('form.loadingTask')}
              value={loadingTaskId}
              onChange={setLoadingTaskId}
              fullWidth
              helperText={t('form.loadingTaskHelper')}
              options={availableLoadingTasks.map((lt) => ({
                value: lt.id,
                label: `${lt.loadingTaskNumber} — ${lt.dispatchOrders.map((o) => o.orderNumber).join(', ') || lt.vehicleNo || ''}`,
              }))}
            />
            {selectedTask && (
              <Typography variant="body2" color="text.secondary">
                {t('form.vehicleNo')}: {selectedTask.vehicleNo ?? '—'}
              </Typography>
            )}
            <TextField label={t('form.driverName')} value={driverName} onChange={(e) => setDriverName(e.target.value)} fullWidth />
            <TextField
              label={t('form.securityRemarks')} value={securityRemarks} onChange={(e) => setSecurityRemarks(e.target.value)}
              fullWidth multiline minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={createMutation.isPending || !loadingTaskId} onClick={() => createMutation.mutate()}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
